import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'

import {
  BUFFER_HOME_TASK_STATUS,
  GetBufferHomeChecklist,
} from './useBufferHomeChecklist'
import type { BufferHomeTaskKey } from './useBufferHomeChecklist'

export const MarkBufferHomeTaskComplete = graphql(/* GraphQL */ `
  mutation MarkBufferHomeTaskComplete(
    $input: MarkBufferHomeTaskCompleteInput!
  ) {
    markBufferHomeTaskComplete(input: $input) {
      ... on MarkBufferHomeTaskCompleteSuccess {
        bufferHomeChecklist {
          tasks {
            taskKey
            status
          }
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UseMarkBufferHomeTaskCompleteResult = {
  markComplete: (taskKey: BufferHomeTaskKey) => void
}

/**
 * Mutation hook for marking a Buffer Home first-steps task as completed.
 * On success, writes the new checklist returned by the server back into
 * the `GetBufferHomeChecklist` cache entry and fires the `Buffer Home
 * Task State Changed` tracking event with the status the task had before
 * the mutation.
 */
export const useMarkBufferHomeTaskComplete =
  (): UseMarkBufferHomeTaskCompleteResult => {
    const organizationId = useOrganizationId()
    const [mark] = useMutation(MarkBufferHomeTaskComplete)

    const markComplete = useCallback(
      (taskKey: BufferHomeTaskKey) => {
        if (!organizationId) return

        void mark({
          variables: { input: { organizationId, taskKey } },
          update: (cache, { data }) => {
            const payload = data?.markBufferHomeTaskComplete
            if (
              !payload ||
              payload.__typename !== 'MarkBufferHomeTaskCompleteSuccess'
            ) {
              return
            }

            // The cache still holds the pre-mutation checklist until the
            // write below, so this is the task's previous status.
            const previous = cache.readQuery({
              query: GetBufferHomeChecklist,
              variables: { input: { organizationId } },
            })
            const previousStatus =
              previous?.bufferHomeChecklist.tasks.find(
                (task) => task.taskKey === taskKey,
              )?.status ?? BUFFER_HOME_TASK_STATUS.Pending

            cache.writeQuery({
              query: GetBufferHomeChecklist,
              variables: { input: { organizationId } },
              data: {
                bufferHomeChecklist: payload.bufferHomeChecklist,
              },
            })

            BufferTracker.bufferHomeTaskStateChanged({
              organizationId,
              clientName: Client.PublishWeb,
              taskKey,
              previousStatus,
              newStatus: BUFFER_HOME_TASK_STATUS.Completed,
            })
          },
        })
      },
      [mark, organizationId],
    )

    return { markComplete }
  }
